import * as anchor from "@coral-xyz/anchor";
import {AnchorProvider, Wallet} from "@coral-xyz/anchor";
import {SolanaTribes} from "./solana_tribes";
import IDL from "./solana_tribes.json"
import {Buffer} from 'buffer';
import {
	ComputeBudgetProgram,
	Connection, MemcmpFilter, PublicKey, TransactionMessage, VersionedTransaction
} from "@solana/web3.js";
import bs58 from 'bs58';
import {ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddress, TOKEN_PROGRAM_ID} from "@solana/spl-token";
import {BLOCK_TIME, PURPOSE} from "@/types/constants";
import {signatureTracking} from "@/composables/useSignatureTracking";
import {toast} from "@/composables/useToast";
import {fogoSession} from "@/composables/useFogoSession";

// Fogo session error code
const FOGO_SESSION_ERROR_CODE_HEX = "0xee6b2800"; // 4000000000 in decimal
const FOGO_SESSION_ERROR_CODE_DEC = 4000000000;

/** Turn getSignatureStatus / RPC `err` into Anchor-style text so parseError + IDL mapping work. */
function formatRpcTransactionErr(err: unknown): string {
	if (err == null) return "Unknown transaction error"
	if (typeof err === "string") return err
	if (typeof err !== "object") return String(err)

	const o = err as Record<string, unknown>

	if (Array.isArray(o.InstructionError) && o.InstructionError.length >= 2) {
		const ix = o.InstructionError[0]
		const inner = o.InstructionError[1]
		if (inner && typeof inner === "object" && inner !== null && "Custom" in inner) {
			const code = (inner as { Custom: number }).Custom
			if (typeof code === "number" && Number.isFinite(code))
				return `custom program error: 0x${code.toString(16)}`
		}
		if (typeof inner === "string") return `Instruction ${ix} failed: ${inner}`
	}

	try {
		return JSON.stringify(err)
	} catch {
		return String(err)
	}
}

export class TribesClient {

	current_slot = {
		slot: 0,
		epoch: 0,
		time: 0,
		// slotTime: 400,
		slotTime: 40,
	}

	world: anchor.web3.PublicKey = anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY;
	worldData = {
		treasury: anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY,
		tokenMint: anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY,
	};

	skipPreflight = true

	connection
	sendTransaction
	session
	program
	sessionPublicKey = PublicKey.default

	wallet

	private _blockInterval: ReturnType<typeof setInterval> | null = null
	private _slotInterval: ReturnType<typeof setInterval> | null = null

	activeUser: anchor.web3.PublicKey = anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY;

	x = true //Stop ts bitching
	userData = {
		tribe: this.x ? null : anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY,
		owner: anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY,
	};


	// @ts-ignore
	tribeData: {
		owner: anchor.web3.PublicKey
	};

	constructor(rpcUrl: string, wallet: Wallet, connection?: Connection, sendTransaction?: any, session?: any) {
		this.wallet = wallet
		this.sendTransaction = sendTransaction
		this.session = session

		if (connection) {
			this.connection = connection
		} else {
			this.connection = new Connection(rpcUrl, 'confirmed');
		}
		const provider = new AnchorProvider(this.connection, wallet, AnchorProvider.defaultOptions())

		anchor.setProvider(provider)

		// @ts-ignore — IDL json lacks Idl.version/name fields expected by Anchor 0.31 types
		// @ts-ignore — IDL json lacks Idl.version/name fields expected by Anchor 0.31 types
		this.program = new anchor.Program<SolanaTribes>(IDL, provider);

		this.getBlock().catch(e => {
			console.warn("Initial getBlock failed, starting intervals anyway:", e)
		}).finally(() => {
			this._blockInterval = setInterval(() => {
				this.getBlock()
			}, 60 * 1000)

			this._slotInterval = setInterval(() => {
				this.current_slot.slot += 2
				this.current_slot.time += this.current_slot.slotTime * 2;
			}, this.current_slot.slotTime * 2)
		})
	}

	destroy() {
		if (this._blockInterval) clearInterval(this._blockInterval)
		if (this._slotInterval) clearInterval(this._slotInterval)
		this._blockInterval = null
		this._slotInterval = null
	}

	/**
	 * Set the world to interact with
	 */
	async setWorld(world: anchor.web3.PublicKey) {
		this.world = world

		// const [pk] = this.userKey(this.wallet.publicKey)
		this.sessionPublicKey = await this.cryptoKeyToSolanaPublicKey(this.session.sessionKey.publicKey)
		const [pk] = this.userKey(this.wallet.publicKey)
		this.activeUser = pk
		console.log("this.activeUser", this.activeUser);
		this.getWorldInfo().then((r: any) => this.worldData = r)
		this.refreshUser()
	}


	async cryptoKeyToSolanaPublicKey(cryptoKey: CryptoKey) {
		const raw = await crypto.subtle.exportKey("raw", cryptoKey);
		const bytes = new Uint8Array(raw);

		if (bytes.length !== 32) {
			throw new Error("Invalid Ed25519 public key length");
		}

		return new PublicKey(bytes);
	}


	/**
	 * Returns if the world is active & set
	 */
	worldSet() {
		return this.worldData.tokenMint !== anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY
	}


	async refreshUser() {
		const userData = await this.getUserInfo()
		this.userData = userData

		if (this.userData.tribe) {
			try {
				const tribeData = await this.getTribeInfo(this.userData.tribe)
				// @ts-ignore
				this.tribeData = tribeData
			} catch (e) {
				console.warn("Failed to fetch tribe info", e)
			}
		}

		return this.userData
	}

	isTribeOwner() {
		if (!this.tribeData || !this.userData)
			return false

		return this.userData.owner.equals(this.tribeData.owner)
	}

	async getWorldInfo() {
		//@ts-ignore
		return await this.program.account.world.fetch(this.world, "confirmed").catch(e => {
			return this.worldData //Not found so just return default
		})
	}

	async getUserInfo() {
		//@ts-ignore
		return await this.program.account.user.fetch(this.activeUser, "confirmed").catch(e => {
			return this.userData //Not found so just return default
		})
	}

	async getTribeInfo(tribe: anchor.web3.PublicKey) {
		//@ts-ignore
		return await this.program.account.tribe.fetch(tribe, "confirmed")
	}

	async getBlock() {
		if (!this.connection) {
			console.warn("Tried to get block, missing connection")
			return
		}

		const epochInfo = await this.connection.getEpochInfo();
		// console.log("Epoch", epochInfo);

		// const block = await this.connection.getSlot(epochInfo.absoluteSlot!, {
		// 	maxSupportedTransactionVersion: 0,
		// })
		// if (!block)
		// 	return

		// this.current_slot.time = block.blockTime!;
		this.current_slot.epoch = epochInfo.epoch;
		this.current_slot.slot = epochInfo.absoluteSlot!;
		this.current_slot.slotTime = BLOCK_TIME;
	}

	getTimeFromBlock(futureBlock: number) {
		const slotDiff = futureBlock - this.current_slot.slot;
		return (slotDiff * this.current_slot.slotTime)
	}

	getTimeBetweenBlock(startBlock: number, futureBlock: number) {
		const slotDiff = futureBlock - startBlock;
		return (slotDiff * this.current_slot.slotTime)
	}

	worldKey(seed: anchor.web3.PublicKey) {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("world"), seed.toBytes()], this.program.programId)
	}

	userKey(wallet: anchor.web3.PublicKey) {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("user"), this.world.toBytes(), wallet.toBytes()], this.program.programId)
	}

	profileKey(wallet: anchor.web3.PublicKey) {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("profile"), wallet.toBytes()], this.program.programId)
	}

	usernameClaimKey(username: string) {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("username"), Buffer.from(username)], this.program.programId)
	}

	tribeInviteKey(tribe: anchor.web3.PublicKey, user: anchor.web3.PublicKey) {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("invite"), tribe.toBytes(), user.toBytes()], this.program.programId)
	}

	treasuryKey(world: anchor.web3.PublicKey): [anchor.web3.PublicKey, number] {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("treasury"), this.program.programId.toBytes(), world.toBytes()], this.program.programId)
	}

	mintAuthorityKey(): [anchor.web3.PublicKey, number] {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("mint_authority"), this.program.programId.toBytes()], this.program.programId)
	}

	villageKey(world: anchor.web3.PublicKey, x: number, y: number) {
		const villageX = new anchor.BN(x).toArrayLike(Buffer, "le", 2);
		const villageY = new anchor.BN(y).toArrayLike(Buffer, "le", 2);
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("village"), world.toBytes(), villageX, villageY], this.program.programId)
	}

	attackKey(attacker: anchor.web3.PublicKey, next_out: number) {
		const nextOut = new anchor.BN(next_out).toArrayLike(Buffer, "le", 1);
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("attack"), attacker.toBytes(), nextOut], this.program.programId)
	}

	supportKey(attacker: anchor.web3.PublicKey, next_out: number) {
		const nextOut = new anchor.BN(next_out).toArrayLike(Buffer, "le", 1);
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("support"), attacker.toBytes(), nextOut], this.program.programId)
	}

	transportKey(village: anchor.web3.PublicKey, next_out: number) {
		const nextOut = new anchor.BN(next_out).toArrayLike(Buffer, "le", 1);
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("transport"), village.toBytes(), nextOut], this.program.programId)
	}

	settleKey(village: anchor.web3.PublicKey, next_out: number) {
		const nextOut = new anchor.BN(next_out).toArrayLike(Buffer, "le", 1);
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("settle"), village.toBytes(), nextOut], this.program.programId)
	}

	tribeKey(name: string) {
		return anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("tribes"), this.world.toBytes(), Buffer.from(name)], this.program.programId)
	}

	async getVillageByCoords(world: anchor.web3.PublicKey, x: number, y: number) {
		const [village] = this.villageKey(world, x, y)
		return await this.getVillage(village)
	}

	async getVillage(village: anchor.web3.PublicKey) {
		//@ts-ignore
		return await this.program.account.village.fetch(village, "confirmed")
	}

	async getUser(owner: anchor.web3.PublicKey) {
		const [uk] = this.userKey(owner)
		//@ts-ignore
		return await this.program.account.user.fetch(uk, "confirmed")
	}

	async getWorld(world: anchor.web3.PublicKey) {
		//@ts-ignore
		return await this.program.account.world.fetch(world, "confirmed")
	}

	async getTribeInvites() {
		//@ts-ignore
		return await this.program.account.invitation.all([{
			memcmp: {
				offset: 8,
				bytes: `${this.world.toString()}`,
			}
		} as MemcmpFilter])
	}


	async getTribeMembers(tribe: anchor.web3.PublicKey) {
		//@ts-ignore
		return await this.program.account.user.all([{
			memcmp: {
				offset: 8 + 32 + 32 + 1,
				bytes: `${tribe.toString()}`,
			}
		} as MemcmpFilter])
	}

	async getVillages(world: anchor.web3.PublicKey) {
		//@ts-ignore
		return await this.program.account.village.all([{
			memcmp: {
				offset: 8,
				bytes: `${world.toString()}`,
			}
		} as MemcmpFilter])
	}

	async getUsers(world: anchor.web3.PublicKey) {
		//@ts-ignore
		return await this.program.account.user.all([{
			memcmp: {
				offset: 8,
				bytes: `${world.toString()}`,
			}
		} as MemcmpFilter])
	}

	async findUserByName(username: string) {
		//@ts-ignore
		return await this.program.account.user.all([{
			memcmp: {
				offset: 8,
				bytes: `${this.world.toString()}`,
			}
		} as MemcmpFilter, {
			memcmp: {
				offset: 8 + 32 + 32 + 33 + 8 + 2 + 1,
				// @ts-ignore
				bytes: bs58.encode(Buffer.from(username, "utf-8")),
			}
		} as MemcmpFilter])
	}

	async getTribes(world: anchor.web3.PublicKey) {
		//@ts-ignore
		return await this.program.account.tribe.all([{
			memcmp: {
				offset: 8,
				bytes: `${world.toString()}`,
			}
		} as MemcmpFilter])
	}

	async indexWorlds() {
		//@ts-ignore
		return await this.program.account.world.all([
			//@ts-ignore
			{
				memcmp: {
					offset: 11,
					bytes: [1],
				}
			} as MemcmpFilter,
		])
	}

	async userVillages(world: anchor.web3.PublicKey, owner: anchor.web3.PublicKey) {
		console.log("Getting villages", world.toString(), owner.toString())

		//@ts-ignore
		return await this.program.account.village.all([
			{
				memcmp: {
					offset: 8,
					bytes: `${world.toString()}`,
				}
			} as MemcmpFilter,
			{
				memcmp: {
					offset: 8 + 32,
					bytes: `${owner.toString()}`,
				}
			} as MemcmpFilter
		])
	}

	async createUser(name: string) {
		const [user, bump] = this.userKey(this.wallet.publicKey)
		console.log("Creating User", {
			world: this.world,
			name: name,
			user: user,
			owner: this.sessionPublicKey.toString(),
		})

		const txn = this.program.methods
			.createUser(bump, name)
			.accounts({
				world: this.world,
				owner: this.sessionPublicKey,
				wallet: this.wallet.publicKey,
				feePayer: this.session.payer,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("createUser error", e)
			throw e
		})

		this.activeUser = user

		return user
	}

	async createVillage(x: number, y: number, name = '', username: string) {
		if (name === '')
			name = `Village (${x},${y})`

		const [village, bump] = this.villageKey(this.world, x, y)
		const [treasury] = this.treasuryKey(this.world)
		console.log(`Village: ${village} - Bump: ${bump}`)

		console.log("Creating village", {
			world: this.world,
			village: village,
			owner: this.sessionPublicKey.toString(),
		})

		const txn = this.program.methods
			.createVillage(x, y, bump, name, username)
			.accounts({
				world: this.world,
				owner: this.sessionPublicKey,
				wallet: this.wallet.publicKey,
				feePayer: this.session.payer,
				treasury: treasury,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("createVillage error", e)
			throw e
		})

		return village
	}

	async updateVillage(village: anchor.web3.PublicKey, name: string) {
		const txn = this.program.methods
			.updateVillage(name)
			.accounts({
				world: this.world,
				village: village,
				owner: this.sessionPublicKey,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("updateVillage error", e)
			throw e
		})

		return village
	}

	async updateUsername(currentUsername: string, newUsername: string) {
		const [profile] = this.profileKey(this.wallet.publicKey)
		const [currentUsernameClaim] = this.usernameClaimKey(currentUsername)
		const [newUsernameClaim] = this.usernameClaimKey(newUsername)

		const txn = this.program.methods
			.updateUsername(currentUsername, newUsername)
			.accounts({
				world: this.world,
				user: this.activeUser,
				profile: profile,
				currentUsernameClaim,
				newUsernameClaim,
				owner: this.sessionPublicKey,
				wallet: this.wallet.publicKey,
				feePayer: this.session.payer,
			} as any)

		await this.signAndSend(txn).catch(e => {
			console.error("updateUsername error", e)
			throw e
		})

		return this.refreshUser()
	}

	async createTribe(name: string) {
		const [tribe, bump] = this.tribeKey(name)
		const [treasury] = this.treasuryKey(this.world)

		console.log("Creating Tribe", {
			world: this.world,
			tribe: tribe,
			tribeName: name,
			owner: this.sessionPublicKey.toString(),
		})

		const txn = this.program.methods
			.createTribe(bump, name)
			.accounts({
				world: this.world,
				//@ts-ignore
				tribe: tribe,
				user: this.activeUser,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
				treasury: treasury,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("createTribe error", e)
			throw e
		})

		this.refreshUser()
		return tribe
	}

	async leaveTribe() {
		const txn = this.program.methods
			.leaveTribe()
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userData.tribe!,
				owner: this.sessionPublicKey,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("leaveTribe error", e)
			throw e
		})

		return this.refreshUser()
	}

	async joinTribe(tribe: anchor.web3.PublicKey, invite: anchor.web3.PublicKey) {
		const txn = this.program.methods
			.joinTribe()
			.accounts({
				world: this.world,
				invitation: invite,
				tribe: tribe,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
				user: this.activeUser,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("leaveTribe error", e)
			throw e
		})

		return this.refreshUser()
	}

	async inviteTribe(targetOwner: anchor.web3.PublicKey) {
		if (!this.userData.tribe)
			throw new Error("You are not part of a tribe")

		const [invitation, bump] = this.tribeInviteKey(this.userData.tribe!, targetOwner)


		//@ts-ignore
		const invite = await this.program.account.invitation.fetch(invitation, "confirmed").catch(e => {
			console.debug("Invite e", e)
		})
		if (invite)
			throw new Error("Invite already exists")

		console.log("Inviting")
		const txn = this.program.methods
			.inviteTribe(bump)
			.accounts({
				world: this.world,
				receiver: targetOwner,
				tribe: this.userData.tribe!,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("inviteTribe error", e)
			throw e
		})

		return this.refreshUser()
	}

	async settleVillage(fromVillage: anchor.web3.PublicKey, nextOut: number, x: number, y: number) {
		const [settlement] = this.settleKey(fromVillage, nextOut)
		console.log(`Settling village at (${x},${y}) - Settlement: ${settlement}`)

		const txn = this.program.methods
			.settleVillage(x, y)
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userTribe(),
				srcVillage: fromVillage,
				settlement: settlement,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("settleVillage error", e)
			throw e
		})

		return settlement
	}

	async settleVillageCheck(settlement: anchor.web3.PublicKey, srcVillage: anchor.web3.PublicKey, x: number, y: number) {
		const [village, bump] = this.villageKey(this.world, x, y)
		console.log(`Completing settlement at (${x},${y}) - Village: ${village}`)

		const txn = this.program.methods
			.settleVillageResult(x, y, bump)
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userTribe(),
				srcVillage: srcVillage,
				settlement: settlement,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
			})

		await this.signAndSend(txn).catch(e => {
			console.error("settleVillageCheck error", e)
			throw e
		})

		return village
	}


	async gatherResources(village: anchor.web3.PublicKey) {
		const txn = this.program.methods
			.gatherResources()
			.accounts({
				world: this.world,
				village: village,
				owner: this.sessionPublicKey,
			})

		return await this.signAndSend(txn)
	}

	async callMilitia(village: anchor.web3.PublicKey) {
		const txn = this.program.methods
			.callMilitia()
			.accounts({
				world: this.world,
				village: village,
				owner: this.sessionPublicKey,
			})

		return await this.signAndSend(txn)
	}


	async refreshVillage(village: anchor.web3.PublicKey) {
		console.log("Refreshing village", village.toString())
		console.time('Refresh Village Duration');
		const txn = this.program.methods
			.refreshVillage()
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userTribe(),
				village: village,
				owner: this.sessionPublicKey,
			})

		const res = await this.signAndSend(txn)
		console.timeEnd('Refresh Village Duration');
		return res
	}


	async upgradeBuilding(village: anchor.web3.PublicKey, building_id: number) {
		console.log("Upgrading building", {village: village.toString(), building_id})
		console.time("Client Upgrade Building Duration");
		const txn = this.program.methods
			.upgradeBuilding(building_id)
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userTribe(),
				village: village,
				owner: this.sessionPublicKey,
			})
			.preInstructions([this.computeBudgetIx(300_000)]);

		console.timeEnd("Client Upgrade Building Duration");

		return await this.signAndSend(txn)
	}


	async trainTroops(village: anchor.web3.PublicKey, troop_type: number, amount: number) {
		const txn = this.program.methods
			.trainTroops(troop_type, amount)
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userTribe(),
				village: village,
				owner: this.sessionPublicKey,
			})
			.preInstructions([this.computeBudgetIx(300_000)]);



		return await this.signAndSend(txn)
	}

	async disbandTroops(village: anchor.web3.PublicKey, troops: Array<number>) {
		if (troops.length !== 9) {
			throw new Error(`troops length invalid: ${troops.length} - Expected: 9`)
		}

		const txn = this.program.methods
			.disbandTroops(troops[0], troops[1], troops[2], troops[3], troops[4], troops[5], troops[6], troops[7], troops[8])
			.accounts({
				world: this.world,
				user: this.activeUser,
				village: village,
				owner: this.sessionPublicKey,
				tribe: this.userTribe(),
			})

		return await this.signAndSend(txn)
	}


	async buildAutoCompleteInstruction(village: anchor.web3.PublicKey, queue_idx: number, is_building: boolean): Promise<anchor.web3.TransactionInstruction> {
		const tokenMint = this.worldData.tokenMint;
		const srcTokenAccount = await getAssociatedTokenAddress(tokenMint, this.wallet.publicKey)

		const tx = this.program.methods
			.autoCompleteQueue(queue_idx, is_building)
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userTribe(),
				village: village,
				owner: this.sessionPublicKey,
				sourceTokenAccount: srcTokenAccount,
				mint: tokenMint,
				mintAuthority: this.worldData.treasury,
			})

		return await tx.instruction()
	}

	async autCompleteQueue(village: anchor.web3.PublicKey, queue_idx: number, is_building: boolean) {
		const tokenMint = this.worldData.tokenMint;
		const srcTokenAccount = await getAssociatedTokenAddress(tokenMint, this.wallet.publicKey)

		const txn = this.program.methods
			.autoCompleteQueue(queue_idx, is_building)
			.accounts({
				world: this.world,
				user: this.activeUser,
				tribe: this.userTribe(),
				village: village,
				owner: this.sessionPublicKey,
				sourceTokenAccount: srcTokenAccount,
				mint: tokenMint,
				mintAuthority: this.worldData.treasury,
			})

		return await this.signAndSend(txn)
	}

	async autCompleteQueueBatch(village: anchor.web3.PublicKey, queueIndices: number[], is_building: boolean) {
		const instructions = await Promise.all(
			queueIndices.map(idx => this.buildAutoCompleteInstruction(village, idx, is_building))
		)
		return await this.sendInstructions(instructions)
	}

	async simulateAttack(village: anchor.web3.PublicKey, villageEnemy: anchor.web3.PublicKey, troops: Array<number>, dTroops: Array<number>) {
		if (troops.length !== 9) {
			throw new Error(`attacking troops length invalid: ${troops.length} - Expected: 9`)
		}
		if (dTroops.length !== 10) {
			throw new Error(`defending troops length invalid: ${dTroops.length} - Expected: 10`)
		}

		const txn = this.program.methods
			.simulateAttackResult(troops[0], troops[1], troops[2], troops[3], troops[4], troops[5], troops[6], troops[7], troops[8], dTroops[0], dTroops[1], dTroops[2], dTroops[3], dTroops[4], dTroops[5], dTroops[6], dTroops[7], dTroops[8], dTroops[9])
			.accounts({
				world: this.world,
				srcVillage: village,
				dstVillage: villageEnemy,
			})

		return await this.signAndSend(txn)
	}

	async attackVillage(village: anchor.web3.PublicKey, villageNextOut: number, enemy: anchor.web3.PublicKey, villageEnemy: anchor.web3.PublicKey, troops: Array<number>) {
		if (troops.length !== 9) {
			throw new Error(`troops length invalid: ${troops.length} - Expected: 9`)
		}

		const [attack] = this.attackKey(village, villageNextOut)
		console.log("Attacking Village", {village, villageEnemy, attack, enemy})

		const txn = this.program.methods
			.sendAttack(troops[0], troops[1], troops[2], troops[3], troops[4], troops[5], troops[6], troops[7], troops[8])
			.accounts({
				world: this.world,
				srcUser: this.activeUser,
				srcTribe: this.userTribe(),
				srcVillage: village,
				dstVillage: villageEnemy,
				dstUser: enemy,
				attack: attack,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
			})

		return await this.signAndSend(txn)
	}


	async defendVillage(village: anchor.web3.PublicKey, villageNextOut: number, supportUser: anchor.web3.PublicKey, villageSupport: anchor.web3.PublicKey, troops: Array<number>) {
		if (troops.length !== 9) {
			throw new Error(`troops length invalid: ${troops.length} - Expected: 9`)
		}

		const [support] = this.supportKey(village, villageNextOut)
		console.log("Supporting Village", {village, villageSupport, support, supportUser})

		const txn = this.program.methods
			.sendSupport(troops[0], troops[1], troops[2], troops[3], troops[4], troops[5], troops[6], troops[7], troops[8])
			.accounts({
				world: this.world,
				srcUser: this.activeUser,
				srcTribe: this.userTribe(),
				srcVillage: village,
				dstVillage: villageSupport,
				dstUser: supportUser,
				support: support,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
			})

		return await this.signAndSend(txn)
	}

	async attackVillageCheck(attack: anchor.web3.PublicKey, village: anchor.web3.PublicKey, villageEnemy: anchor.web3.PublicKey, dstUser: anchor.web3.PublicKey, dstTribe: anchor.web3.PublicKey | null = null) {
		const txn = this.program.methods
			.attackVillageResult()
			.accounts({
				world: this.world,
				srcUser: this.activeUser,
				dstUser: dstUser,
				srcTribe: this.userTribe(),
				dstTribe: dstTribe,
				srcVillage: village,
				dstVillage: villageEnemy,
				attackingUser: this.wallet.publicKey, //TODO adjust so others can check attacks
				attack: attack,
				owner: this.sessionPublicKey,
			})

		return await this.signAndSend(txn)
	}

	async supportVillageCheck(support: anchor.web3.PublicKey, village: anchor.web3.PublicKey, villageSupport: anchor.web3.PublicKey, dstUser: anchor.web3.PublicKey, dstTribe: anchor.web3.PublicKey | null = null) {
		const txn = this.program.methods
			.supportVillageResult()
			.accounts({
				world: this.world,
				srcUser: this.activeUser,
				dstUser: dstUser,
				srcTribe: this.userTribe(),
				dstTribe: dstTribe,
				srcVillage: village,
				dstVillage: villageSupport,
				supportingUser: this.wallet.publicKey, //TODO adjust so others can check attacks
				support: support,
				owner: this.sessionPublicKey,
			})

		return await this.signAndSend(txn)
	}

	async returnVillageCheck(support: anchor.web3.PublicKey, village: anchor.web3.PublicKey) {
		const txn = this.program.methods
			.returnTroopsResult()
			.accounts({
				srcVillage: village,
				movement: support,
				owner: this.sessionPublicKey,
				attackingUser: this.wallet.publicKey,
			})

		return await this.signAndSend(txn)
	}

	async recallSupport(srcVillage: anchor.web3.PublicKey, dstVillage: anchor.web3.PublicKey, support: anchor.web3.PublicKey) {
		const txn = this.program.methods
			.recallSupport()
			.accounts({
				srcVillage: srcVillage,
				dstVillage: dstVillage,
				support: support,
				owner: this.sessionPublicKey,
			})

		return await this.signAndSend(txn)
	}

	async sendResources(srcVillage: anchor.web3.PublicKey, nextOut: number, dstVillage: anchor.web3.PublicKey, dstUser: anchor.web3.PublicKey, food: number, gold: number, mana: number) {
		const [transport] = this.transportKey(srcVillage, nextOut)
		console.log("Sending Resources", {srcVillage, dstVillage, transport, food, gold, mana})

		const txn = this.program.methods
			.sendResources(new anchor.BN(food), new anchor.BN(gold), new anchor.BN(mana))
			.accounts({
				world: this.world,
				srcVillage: srcVillage,
				srcUser: this.activeUser,
				transport: transport,
				dstVillage: dstVillage,
				dstUser: dstUser,
				owner: this.sessionPublicKey,
				feePayer: this.session.payer,
				tribe: this.userTribe()
			})


		return await this.signAndSend(txn)
	}

	async transportArrivalCheck(transport: anchor.web3.PublicKey, srcVillage: anchor.web3.PublicKey, dstVillage: anchor.web3.PublicKey, dstUser: anchor.web3.PublicKey, dstTribe: anchor.web3.PublicKey | null = null) {
		const txn = this.program.methods
			.transportArrivalResult()
			.accounts({
				world: this.world,
				srcVillage: srcVillage,
				dstVillage: dstVillage,
				dstUser: dstUser,
				transport: transport,
				owner: this.wallet.publicKey,
				sendingUser: this.wallet.publicKey,
				dstTribe: dstTribe,
			})

		return await this.signAndSend(txn)
	}

	async resizeVillage(village: anchor.web3.PublicKey) {
		const txn = this.program.methods
			.resizeVillage()
			.accounts({
				village: village,
				owner: this.wallet.publicKey,
			})

		return await this.signAndSend(txn)
	}


	async getAttacks(attacker: anchor.web3.PublicKey) {
		//@ts-ignore
		const attacks = await this.program.account.troopMovement.all([{
			memcmp: {
				offset: 8 + 32,
				bytes: `${attacker.toString()}`,
			}
		} as MemcmpFilter])

		//@ts-ignore
		const defence = await this.program.account.troopMovement.all([{
			memcmp: {
				offset: 8 + 32 + 32,
				bytes: `${attacker.toString()}`,
			}
		} as MemcmpFilter])

		return attacks.concat(defence)
	}


	async getActiveAttacks(attacker: anchor.web3.PublicKey) {
		const outbound = await this.program.account.troopMovement.all([{
			memcmp: {
				offset: 8 + 32, //Attacker
				bytes: `${attacker.toString()}`,
			}
		} as MemcmpFilter])

		const inbound = await this.program.account.troopMovement.all([{
			memcmp: {
				offset: 8 + 32 + 32, //Defender
				bytes: `${attacker.toString()}`,
			}
		} as MemcmpFilter])
		return outbound.concat(inbound)
	}

	async getAttack(attack: anchor.web3.PublicKey) {
		//@ts-ignore
		return this.program.account.troopMovement.fetch(attack)
	}

	async getDefence(defender: anchor.web3.PublicKey) {
		//@ts-ignore
		return this.program.account.troopMovement.all([{
			memcmp: {
				offset: 8 + 32 + 32,
				bytes: `${defender.toString()}`,
			}
		} as MemcmpFilter])
	}

	async getAttackLog(attack: anchor.web3.PublicKey) {
		const attackSigs = await this.program.provider.connection.getSignaturesForAddress(attack, {
			limit: 5
		})
		console.log("attackSigs", attackSigs.map((a) => a.signature))

		for (const sig of attackSigs) {
			const txn = await this.program.provider.connection.getTransaction(sig.signature).catch(e => {
				console.error("getTransaction error", e)
				return null
			})
			if (!txn) continue

			const parsed = this._parseAttackLog(txn)
			if (parsed.valid) {
				return parsed
			}
		}

		return {
			valid: false
		}
	}

	async getAttackLogFromSignature(sig: anchor.web3.TransactionSignature) {
		const txn = await this.program.provider.connection.getTransaction(sig, {commitment: 'confirmed', maxSupportedTransactionVersion: 0}).catch(e => {
			console.error("getTransaction error", e)
			return null
		})
		if (!txn) return {
			valid: false
		}

		const parsed = this._parseAttackLog(txn)
		if (parsed.valid)
			return parsed

		return {
			valid: false
		}
	}

	async parseSimulationLog(sig: string) {
		const txn = await this.program.provider.connection.getTransaction(sig, {commitment: 'confirmed', maxSupportedTransactionVersion: 0}).catch(e => {
			console.error("parseSimulationLog getTransaction error", e)
			return null
		})
		if (!txn)
			return {
				valid: false
			}
		return this._parseAttackLog(txn)
	}

	_parseAttackLog(txn: any) {
		const msg = txn.transaction.message;
		const accountKeys = msg.accountKeys ?? msg.staticAccountKeys;
		const instructions = msg.instructions ?? msg.compiledInstructions ?? [];
		const programId = this.program.programId.toString();
		let accounts: any = null;

		for (const ix of instructions) {
			if (accountKeys[ix.programIdIndex]?.toString() !== programId) continue;

			try {
				const ixData = ix.data instanceof Uint8Array ? ix.data : Buffer.from(bs58.decode(ix.data));
				const decoded = (this.program.coder.instruction as any).decode(ixData);
				if (!decoded) continue;

				const ixAccountIndices = ix.accounts ?? ix.accountKeyIndexes ?? [];
				const ixKeys = ixAccountIndices.map((idx: number) => accountKeys[idx]);

				switch (decoded.name) {
					case 'attackVillageResult':
						accounts = {
							purpose: {[PURPOSE.attack]: true},
							srcVillage: ixKeys[1],
							dstVillage: ixKeys[2],
							srcUser: ixKeys[3],
						};
						break;
					case 'supportVillageResult':
						accounts = {
							purpose: {[PURPOSE.support]: true},
							srcVillage: ixKeys[1],
							dstVillage: ixKeys[2],
							srcUser: ixKeys[3],
						};
						break;
					case 'returnTroopsResult':
						accounts = {
							purpose: {[PURPOSE.return]: true},
							srcVillage: ixKeys[1],
						};
						break;
					case 'simulateAttackResult':
						accounts = {
							purpose: {[PURPOSE.simulate]: true},
							srcVillage: ixKeys[1],
							dstVillage: ixKeys[2],
						};
						break;
					case 'transportArrivalResult':
						accounts = {
							purpose: {[PURPOSE.transport]: true},
							srcVillage: ixKeys[1],
							dstVillage: ixKeys[2],
						};
						break;
					case 'settleVillageResult':
						accounts = {
							purpose: {[PURPOSE.settle]: true},
							srcVillage: ixKeys[1],
							dstVillage: ixKeys[4],
						};
						break;
				}

				if (accounts) {
					break;
				}
			} catch (e) {
				console.error("Failed to decode instruction", e);
			}
		}

		const before = {
			defender: [],
			attacker: [],
		};
		const after = {
			defender: [],
			attacker: [],
		};

		const wall = {
			amount: 0,
			from: 0,
			to: 0,
		}

		const catapult = {
			target: "",
			amount: 0,
			from: 0,
			to: 0,
		}

		const takeover = {
			user: null,
		}

		const resources = {
			gold: 0,
			mana: 0,
			food: 0,
		}

		let luck = 0
		let winner = "defender"

		const troopTypes = [
			'spearFighter',
			'swordsman',
			'axeman',
			'archer',
			'light_cavalry',
			'heavy_cavalry',
			'ram',
			'catapult',
			'settler',
			'militia',
		];
		for (const l of txn.meta.logMessages) {
			if (l.indexOf("0xBDEF:") > -1 || l.indexOf("0xBATK:") > -1) {
				const isDef = l.indexOf("0xBDEF:") > -1
				const prefix = isDef ? "0xBDEF:" : "0xBATK:"
				const ns = l.substring(l.indexOf(prefix) + prefix.length)
				if (ns !== "") {
					const target = isDef ? before.defender : before.attacker
					for (const tunit of ns.split(",")) {
						const [n, amount] = tunit.split(":")
						const tt = {};
						//@ts-ignore
						tt[troopTypes[n]] = true
						//@ts-ignore
						target.push({troopType: tt, count: parseInt(amount)})
					}
				}
				continue
			}
			if (l.indexOf("0xADEF:") > -1 || l.indexOf("0xAATK:") > -1) {
				const isDef = l.indexOf("0xADEF:") > -1
				const prefix = isDef ? "0xADEF:" : "0xAATK:"
				const ns = l.substring(l.indexOf(prefix) + prefix.length)
				if (ns !== "") {
					const target = isDef ? after.defender : after.attacker
					for (const tunit of ns.split(",")) {
						const [n, amount] = tunit.split(":")
						const tt = {};
						//@ts-ignore
						tt[troopTypes[n]] = true
						//@ts-ignore
						target.push({troopType: tt, count: parseInt(amount)})
					}
				}
				continue
			}
			if (l.indexOf("0xLK:") > -1) { //Luck
				const ns = l.substring(l.indexOf("0xLK:") + 5)
				if (ns !== "") {
					luck = parseInt(ns)
				}
				continue
			}
			if (l.indexOf("0xBW:") > -1) { //Wall
				const ns = l.substring(l.indexOf("0xBW:") + 5)
				if (ns !== "") {
					const [amnt, from, to] = ns.split(":")
					wall.amount = parseInt(amnt)
					wall.from = parseInt(from)
					wall.to = parseInt(to)
				}
				continue
			}
			if (l.indexOf("0xBC:") > -1) { //Catapult
				const ns = l.substring(l.indexOf("0xBC:") + 5)
				if (ns !== "") {
					const BUILDING_NAMES = [
						'HQ', 'Farm', 'Mine', 'Mana', 'Warehouse',
						'Barracks', 'Stable', 'Siege', 'Wall', 'Market'
					]
					const [targetIdx, amnt, from, to] = ns.split(":")
					catapult.target = BUILDING_NAMES[parseInt(targetIdx)] || 'Unknown'
					catapult.amount = parseInt(amnt)
					catapult.from = parseInt(from)
					catapult.to = parseInt(to)
				}
				continue
			}
			if (l.indexOf("0xBT:") > -1) {
				const ns = l.substring(l.indexOf("0xBT:") + 5)
				if (ns !== "") {
					takeover.user = ns
				}
				continue
			}

			if (l.indexOf("0xAR:") > -1) { //Resources
				const ns = l.substring(l.indexOf("0xAR:") + 5)
				if (ns !== "") {
					const [food, gold, mana] = ns.split(":")
					resources.mana = Number(mana)
					resources.gold = Number(gold)
					resources.food = Number(food)
				}
				continue
			}
			if (l.indexOf("Removing resources:") > -1) {
				const match = l.match(/M:(\d+)\s+G:(\d+)\s+F:(\d+)/);
				if (match) {
					resources.mana = Number(match[1])
					resources.gold = Number(match[2])
					resources.food = Number(match[3])
				}
				continue
			}
			if (l.indexOf("Attacker wins") > -1) {
				winner = "attacker"
			}
			if (l.indexOf("Transport arrived") > -1) {
				const match = l.match(/F:(\d+)\s+G:(\d+)\s+M:(\d+)/);
				if (match) {
					resources.food = Number(match[1])
					resources.gold = Number(match[2])
					resources.mana = Number(match[3])
				}
				continue
			}
		}

		const isTransport = accounts && accounts.purpose && accounts.purpose[PURPOSE.transport] === true
		const hasTroopData = before.defender.length + before.attacker.length + after.defender.length + after.attacker.length > 0
		return {
			valid: isTransport ? accounts != null : hasTroopData,
			accounts: accounts,
			before: before,
			after: after,
			wall: wall,
			catapult: catapult,
			takeover: takeover,
			resources: resources,
			luck: luck,
			winner: winner,
		}
	}


	async surroundingVillages(tilePublicKeys: anchor.web3.PublicKey[]) {
		// Fetch account data for all tiles

		//@ts-ignore
		return this.program.account.village.fetchMultiple(tilePublicKeys)
	}

	/**
	 * @param village
	 * @param id
	 * @param amount
	 */
	async tokenToResources(village: anchor.web3.PublicKey, id: number, amount: number) {
		const tokenAta = await getAssociatedTokenAddress(this.worldData.tokenMint, this.wallet.publicKey)
		const [activeTreasury] = this.treasuryKey(this.world)

		console.log("tokenToResources", {village, id, amount})
		const txn = this.program.methods.exchangeTokensForResources(new anchor.BN(amount), id, 0)
			.accounts({
				world: this.world,
				village: village,
				sourceTokenAccount: tokenAta,
				destinationTokenAccount: tokenAta,
				owner: this.sessionPublicKey,
				// @ts-ignore
				mint: this.worldData.tokenMint,
				mintAuthority: activeTreasury,
			})

		return await this.signAndSend(txn)
	}

	/**
	 * @param village
	 * @param id
	 * @param amount
	 */
	async tokenToResourceInstruction(village: anchor.web3.PublicKey, id: number, amount: number): Promise<anchor.web3.TransactionInstruction> {
		const tokenAta = await getAssociatedTokenAddress(this.worldData.tokenMint, this.wallet.publicKey)
		const [activeTreasury,] = this.treasuryKey(this.world)

		const tx = this.program.methods.exchangeTokensForResources(new anchor.BN(amount), id, 0)
			.accounts({
				world: this.world,
				village: village,
				sourceTokenAccount: tokenAta,
				destinationTokenAccount: tokenAta,
				owner: this.sessionPublicKey,
				// @ts-ignore
				mint: this.worldData.tokenMint,
				mintAuthority: activeTreasury,
			})

		return await tx.instruction()
	}

	async sendInstructions(ixs: anchor.web3.TransactionInstruction[]) {
		const txn = new anchor.web3.Transaction()
		txn.add(...ixs)

		return await this._signAndSend(txn)
	}

	async mintTokens(tokenMint: anchor.web3.PublicKey, amount: number) {
		const associatedDestinationTokenAddr = await getAssociatedTokenAddress(tokenMint, this.wallet.publicKey)

		const txn = this.program.methods.mintTokens(new anchor.BN(amount))
			.accounts({
				world: this.world,
				wallet: this.wallet.publicKey,
				feePayer: this.session.payer,
				owner: this.sessionPublicKey,
				mint: tokenMint,
				destinationTokenAccount: associatedDestinationTokenAddr,
			})

		return await this.signAndSend(txn)
	}

	async buyAeonite(packageId: number, paymentMint: anchor.web3.PublicKey) {
		const sourcePaymentAccount = await getAssociatedTokenAddress(paymentMint, this.wallet.publicKey)
		const [activeTreasury] = this.treasuryKey(this.world)
		const treasuryPaymentAccount = await getAssociatedTokenAddress(paymentMint, activeTreasury, true)
		const destinationTokenAccount = await getAssociatedTokenAddress(this.worldData.tokenMint, this.wallet.publicKey)

		const txn = this.program.methods.buyAeonite(packageId)
			.accounts({
				world: this.world,
				sourcePaymentAccount: sourcePaymentAccount,
				// @ts-ignore
				treasuryPaymentAccount: treasuryPaymentAccount,
				destinationTokenAccount: destinationTokenAccount,
				feePayer: this.session.payer,
				owner: this.sessionPublicKey,
				wallet: this.wallet.publicKey,
				aeonMint: this.worldData.tokenMint,
				paymentMint: paymentMint,
				treasury: activeTreasury,
			})

		return await this.signAndSend(txn)
	}

	async buyShield(packageId: number) {
		const sourceTokenAccount = await getAssociatedTokenAddress(this.worldData.tokenMint, this.wallet.publicKey)
		const [programSigner] = anchor.web3.PublicKey.findProgramAddressSync(
			[Buffer.from("fogo_session_program_signer")],
			this.program.programId
		)

		const txn = this.program.methods.buyShield(packageId)
			.accounts({
				world: this.world,
				sourceTokenAccount: sourceTokenAccount,
				user: this.activeUser,
				owner: this.sessionPublicKey,
				mint: this.worldData.tokenMint,
				// @ts-ignore
				programSigner: programSigner,
			})

		return await this.signAndSend(txn)
	}

	async buyPremium() {
		const sourceTokenAccount = await getAssociatedTokenAddress(this.worldData.tokenMint, this.wallet.publicKey)
		const [programSigner] = anchor.web3.PublicKey.findProgramAddressSync(
			[Buffer.from("fogo_session_program_signer")],
			this.program.programId
		)

		const txn = this.program.methods.buyPremium()
			.accounts({
				world: this.world,
				sourceTokenAccount: sourceTokenAccount,
				user: this.activeUser,
				owner: this.sessionPublicKey,
				mint: this.worldData.tokenMint,
				// @ts-ignore
				programSigner: programSigner,
			})

		return await this.signAndSend(txn)
	}

	async signAndSend(txn: any) {
		const transaction = await txn.transaction()
		return this._signAndSend(transaction)
	}

	async _signAndSend(transaction: anchor.web3.Transaction) {
		if (this.sendTransaction) {
			const txn = await this.convertToVersionedTransaction(transaction, this.session.payer)
			await this.signVersionedTransaction(txn);
			const {data: sig, error} = await this.sendTransaction(
				{transaction: txn}
			)
			console.log("SIG", sig)
			if (error) {
				throw error
			}
			await new Promise<void>((resolve, reject) => {
				signatureTracking.listen(sig, async (_, status) => {
					console.log("Signature status", sig, status)
					if (status.status === 'error' || status.status === 'timeout') {
						const errorMsg =
							status.err != null
								? formatRpcTransactionErr(status.err)
								: `Transaction failed: ${status.status}`
						const err = new Error(errorMsg) as any
						err.sig = sig
						reject(err)
					} else if (status.status === 'confirmed' || status.status === 'finalized') {
						resolve()
					}
				});
			});
			return sig
		}

		const hash = await this.connection.getLatestBlockhash('confirmed')

		transaction.recentBlockhash = hash.blockhash
		transaction.feePayer = this.wallet.publicKey
		const signedTxn = await this.wallet.signTransaction(transaction)

		//@ts-ignore
		const sig = await this.connection.sendRawTransaction(signedTxn.serialize(), {skipPreflight: this.skipPreflight})

		console.log("SIG", sig)

		await this.waitForProcessedSignatureStatus(sig);

		// await this.connection.confirmTransaction(sig, "confirmed")
		return sig
	}

	async waitForProcessedSignatureStatus(signature: string, maxWaitMs = 30_000) {
		const deadline = Date.now() + maxWaitMs
		// eslint-disable-next-line
		while (true) {
			if (Date.now() > deadline) {
				throw new Error(`Transaction confirmation timed out after ${maxWaitMs / 1000}s`)
			}

			try {
				const resp = await this.connection.getSignatureStatus(signature, {
					searchTransactionHistory: false,
				})

				if (!resp?.value) {
					await new Promise(r => setTimeout(r, 2000));
					continue
				}

				if (resp.value.err) {
					const msg = formatRpcTransactionErr(resp.value.err)
					throw new Error(msg)
				}

				return
			} catch (e) {
				const msg = e instanceof Error ? e.message : `${e}`
				this.toastError(msg)
				throw e
			}
		}
	}

	toastError(err: string) {
		const s = err.toString()
		const lookFor = "custom program error: ";
		const ix = s.indexOf(lookFor);
		
		if (ix !== -1) {
			let errCode = s.substring(ix + lookFor.length).trim()
			const pipeIndex = errCode.indexOf(" |");
			if (pipeIndex > 0) {
				errCode = errCode.substring(0, pipeIndex).trim();
			}
			
			if (errCode === FOGO_SESSION_ERROR_CODE_HEX) {
				fogoSession.triggerReconnect().catch(e => 
					console.error("[TribesClient] triggerReconnect failed", e)
				);
				return; // Don't show toast for session errors
			}
		}
		
		const parsed = this.parseError(err)
		toast.error(parsed, "Transaction failed")
	}

	async convertToVersionedTransaction(
		transaction: anchor.web3.Transaction,
		payer: PublicKey
	): Promise<VersionedTransaction> {
		const hash = await this.connection.getLatestBlockhash('confirmed')

		const message = new TransactionMessage({
			payerKey: payer,
			recentBlockhash: hash.blockhash,
			instructions: transaction.instructions,
		}).compileToV0Message();

		const txn = new VersionedTransaction(message)

		return txn;
	}

	async signVersionedTransaction(
		txn: VersionedTransaction,
	) {
		const { numRequiredSignatures } = txn.message.header;
		const requiredKeys = txn.message.staticAccountKeys.slice(0, numRequiredSignatures);
		if (!requiredKeys.some(k => k.equals(this.sessionPublicKey))) return txn;

		const messageBytes = new Uint8Array(txn.message.serialize());

		const signature = await crypto.subtle.sign(
			"Ed25519",
			this.session.sessionKey.privateKey,
			messageBytes
		);

		txn.addSignature(this.sessionPublicKey, new Uint8Array(signature));

		return txn;
	}

	parseError(e: string): string {
		const s = e.toString()
		const lookFor = "custom program error: ";
		const ix = s.indexOf(lookFor);
		if (ix === -1)
			return e

		let errCode = s.substring(ix + lookFor.length).trim()
		
		const pipeIndex = errCode.indexOf(" |");
		if (pipeIndex > 0) {
			errCode = errCode.substring(0, pipeIndex).trim();
		}
		
		const decCode = parseInt(errCode, 16);
		const cerr = this.program.idl.errors?.find((err: any) => err.code === decCode || err.code === decCode * 2)
		return cerr?.msg || e
	}

	toPublicKey(str: string): anchor.web3.PublicKey {
		return new anchor.web3.PublicKey(str)
	}

	toName(str: string): string {
		const b = Buffer.from(str)
		const i = b.indexOf(0x00)

		const end = i === -1 ? b.length : i

		return b.subarray(0, end).toString('utf8').trim()
	}

	userTribe() {
		return this.userData.tribe || this.defaultTribe()
	}

	defaultTribe() {
		const [pk] = anchor.web3.PublicKey.findProgramAddressSync([Buffer.from("tribes"), this.world.toBytes()], this.program.programId)
		return pk
	}

	async getAeonTokenAccount(tokenMint: anchor.web3.PublicKey) {
		return await getAssociatedTokenAddress(tokenMint, this.wallet.publicKey)
	}

	computeBudgetIx(units: number) {
		return ComputeBudgetProgram.setComputeUnitLimit({
			units: units,
		})
	}
}
